// src/pages/SubscriptionDetail.jsx
// Modified to use server-side OG generation
// Social media scrapers get OG tags from API, not from client-side Helmet

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  FiArrowLeft,
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiCheckCircle,
  FiUsers,
  FiStar,
  FiArrowRight,
  FiTrendingUp,
  FiZap,
  FiAward,
  FiFolder,
  FiExternalLink,
  FiMessageCircle,
  FiInfo,
  FiSend,
  FiAlertCircle,
  FiHeart,
  FiShield,
  FiGrid,
  FiPackage,
  FiShare2,
  FiAlertTriangle,
  FiCheck,
  FiPhone,
  FiMail,
  FiX,
  FiCopy,
  FiTwitter,
  FiFacebook,
  FiLinkedin,
  FiInstagram,
  FiLink,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { TbBrandFiverr } from 'react-icons/tb';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  db,
  collection,
  getDocs,
  query,
  where,
  orderBy,
} from '../services/firebase';
import SubscriptionDetailSkeleton from '../components/subscription/SubscriptionDetailSkeleton';
import { EmailValidationInput } from '../utils/emailValidation.jsx';
import { subscriptionService } from '../services/subscriptionService';
import { useTheme } from '../contexts/ThemeContext';
import { FiverrCard } from '../components/FiverrCard';
// ✅ Import simplified ogImageService for share modal ONLY
import { ogImageService } from '../services/ogImageService';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const DEFAULT_IMAGE = '/og-images/default/og-image-default.png';

// =============================================
// ICON MAPPER
// =============================================
const getIconComponent = iconName => {
  const icons = {
    FiCalendar,
    FiZap,
    FiStar,
    FiHeart,
    FiTrendingUp,
    FiUsers,
    FiAward,
    FiShield,
    FiPackage,
    FiGrid,
    TbBrandFiverr,
  };
  return icons[iconName] || FiCalendar;
};

// =============================================
// SOCIAL SHARE MODAL - Uses simplified ogImageService
// =============================================
const SocialShareModal = ({ isOpen, onClose, plan, shareUrl }) => {
  const [copied, setCopied] = useState(false);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${shareUrl}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'subscription_plan',
          content_id: plan?.id,
          content_title: plan?.name,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  // ✅ Use simplified ogImageService for modal preview ONLY
  const shareData = useMemo(() => {
    if (!plan) return null;
    return ogImageService.subscription(plan);
  }, [plan]);

  if (!isOpen || !plan || !shareData) return null;

  const sharePlatforms = [
    {
      id: 'twitter',
      name: 'Twitter',
      icon: FiTwitter,
      color: '#1DA1F2',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(plan.name)}&url=${encodeURIComponent(shareUrl)}&via=wdhejbul`,
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: FiFacebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(plan.name)}`,
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: FiLinkedin,
      color: '#0A66C2',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: FaWhatsapp,
      color: '#25D366',
      url: `https://api.whatsapp.com/send?text=${encodeURIComponent(`📝 ${plan.name}\n\n${shareUrl}`)}`,
    },
    {
      id: 'telegram',
      name: 'Telegram',
      icon: FiSend,
      color: '#26A5E4',
      url: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(`📝 ${plan.name}`)}`,
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: FiInstagram,
      color: '#E4405F',
      url: null,
      action: () => {
        copyToClipboard();
        alert('📸 Instagram: Link copied! Open Instagram app to share.');
        trackShare('instagram');
      },
    },
    {
      id: 'email',
      name: 'Email',
      icon: FiMail,
      color: '#EA4335',
      url: `mailto:?subject=${encodeURIComponent(`Check out: ${plan.name}`)}&body=${encodeURIComponent(`I thought you might find this interesting:\n\n${plan.name}\n${shareUrl}`)}`,
    },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={{ animation: 'fadeIn 0.2s ease-out' }}
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl"
        style={{
          backgroundColor: isDark
            ? 'rgba(20,20,20,0.98)'
            : 'rgba(255,255,255,0.98)',
          backdropFilter: 'blur(20px)',
          border: isDark
            ? '1px solid rgba(255,255,255,0.08)'
            : '1px solid rgba(0,0,0,0.06)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 px-6 py-4 border-b flex items-center justify-between"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
            backgroundColor: isDark
              ? 'rgba(20,20,20,0.95)'
              : 'rgba(255,255,255,0.95)',
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.05)'
                  : 'rgba(0,0,0,0.05)',
              }}
            >
              <FiShare2 className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h3
                className="text-lg font-semibold"
                style={{ color: 'var(--text)' }}
              >
                Share this plan
              </h3>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>
                Spread the word about "{plan.name}"
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Preview Card - Uses simplified shareData */}
        <div className="px-6 pt-4">
          <div
            className="rounded-xl overflow-hidden border"
            style={{
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
            }}
          >
            <div className="relative aspect-[1.91/1] bg-gray-100 dark:bg-gray-800">
              <img
                src={shareData.image || DEFAULT_IMAGE}
                alt={plan.name}
                className="w-full h-full object-cover"
                onError={e => {
                  e.target.src = DEFAULT_IMAGE;
                }}
              />
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent">
                <p className="text-white font-semibold text-lg line-clamp-1">
                  {shareData.title || plan.name}
                </p>
                <p className="text-white/70 text-sm line-clamp-1">
                  {shareData.description || plan.description}
                </p>
                <p className="text-white/40 text-xs mt-0.5">{shareUrl}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons Grid */}
        <div className="px-6 py-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {sharePlatforms.map(platform => {
              const Icon = platform.icon;
              const isDisabled = !platform.url && !platform.action;

              return (
                <button
                  key={platform.id}
                  onClick={() => {
                    if (platform.action) {
                      platform.action();
                    } else if (platform.url) {
                      window.open(
                        platform.url,
                        '_blank',
                        'noopener,noreferrer,width=600,height=400',
                      );
                      trackShare(platform.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl transition-all duration-200 ${
                    isDisabled
                      ? 'opacity-40 cursor-not-allowed'
                      : 'hover:scale-105 hover:shadow-md active:scale-95'
                  }`}
                  style={{
                    backgroundColor: isDark
                      ? 'rgba(255,255,255,0.03)'
                      : 'rgba(0,0,0,0.02)',
                    border: isDark
                      ? '1px solid rgba(255,255,255,0.06)'
                      : '1px solid rgba(0,0,0,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{
                      backgroundColor: `${platform.color}15`,
                      color: platform.color,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className="text-xs font-medium"
                    style={{ color: 'var(--text)' }}
                  >
                    {platform.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Copy Link Section */}
        <div
          className="px-6 pb-6 border-t"
          style={{
            borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
          }}
        >
          <div className="pt-4">
            <div
              className="flex items-center gap-2 p-2 rounded-xl border"
              style={{ borderColor: 'var(--border)' }}
            >
              <FiLink
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--muted)' }}
              />
              <p
                className="text-xs truncate flex-1"
                style={{ color: 'var(--muted)' }}
              >
                {shareUrl}
              </p>
              <button
                onClick={copyToClipboard}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  copied
                    ? 'text-green-500 bg-green-500/10'
                    : 'hover:bg-black/5 dark:hover:bg-white/5'
                }`}
                style={{ color: copied ? '#22c55e' : 'var(--text)' }}
              >
                {copied ? (
                  <>
                    <FiCheckCircle className="w-3.5 h-3.5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <FiCopy className="w-3.5 h-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// =============================================
// SHARE BUTTONS - Updated with Modal
// =============================================
const ShareButtons = ({ url, title, description, imageUrl, plan }) => {
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackShare('copy');
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const trackShare = async platform => {
    try {
      console.log(`Shared on ${platform}: ${url}`);
      if (window.gtag) {
        window.gtag('event', 'share', {
          method: platform,
          content_type: 'subscription_plan',
          content_id: plan?.id,
          content_title: title,
        });
      }
    } catch (error) {
      console.error('Share tracking failed:', error);
    }
  };

  const quickShare = platform => {
    const encodedTitle = encodeURIComponent(`📝 ${title}`);
    const encodedUrl = encodeURIComponent(url);

    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}&via=wdhejbul`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedTitle}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%0A${encodedUrl}`,
      telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
      instagram: null,
      email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
    };

    if (platform === 'instagram') {
      copyToClipboard();
      alert('📸 Instagram: Link copied! Open Instagram app to share.');
      trackShare('instagram');
      return;
    }

    const shareUrl = urls[platform];
    if (shareUrl) {
      window.open(
        shareUrl,
        '_blank',
        'noopener,noreferrer,width=600,height=400',
      );
      trackShare(platform);
    }
  };

  const quickPlatforms = [
    { id: 'twitter', icon: FiTwitter, color: '#1DA1F2' },
    { id: 'facebook', icon: FiFacebook, color: '#1877F2' },
    { id: 'linkedin', icon: FiLinkedin, color: '#0A66C2' },
    { id: 'whatsapp', icon: FaWhatsapp, color: '#25D366' },
    { id: 'telegram', icon: FiSend, color: '#26A5E4' },
  ];

  return (
    <>
      <div className="flex items-center gap-1.5">
        <button
          onClick={copyToClipboard}
          className={`p-1.5 rounded-lg transition-all duration-200 cursor-pointer ${
            copied
              ? 'text-green-500 bg-green-500/10'
              : 'hover:bg-black/5 dark:hover:bg-white/5'
          }`}
          style={{ color: copied ? '#22c55e' : 'var(--text)' }}
          aria-label="Copy link"
          title="Copy link"
        >
          {copied ? (
            <FiCheckCircle className="w-4 h-4" />
          ) : (
            <FiCopy className="w-4 h-4" />
          )}
        </button>

        {quickPlatforms.map(platform => {
          const Icon = platform.icon;
          return (
            <button
              key={platform.id}
              onClick={() => quickShare(platform.id)}
              className="p-1.5 rounded-lg transition-all duration-200 cursor-pointer hover:scale-110 hover:bg-black/5 dark:hover:bg-white/5"
              style={{ color: 'var(--text)' }}
              aria-label={`Share on ${platform.id}`}
              title={`Share on ${platform.id}`}
            >
              <Icon className="w-4 h-4" />
            </button>
          );
        })}

        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer text-sm"
          style={{ color: 'var(--text)' }}
          aria-label="More share options"
          title="More share options"
        >
          <FiShare2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </button>
      </div>

      {showModal && (
        <SocialShareModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          plan={plan}
          shareUrl={url}
        />
      )}
    </>
  );
};

// =============================================
// WORKFLOW TOOLTIP - Enhanced with Email Guidelines
// =============================================
const WorkflowTooltip = () => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="relative inline-block" ref={tooltipRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
        className="flex items-center gap-1.5 text-xs transition-colors hover:opacity-70"
        style={{ color: 'var(--muted)' }}
      >
        <FiInfo className="w-3.5 h-3.5" />
        <span>How it works</span>
        <FiArrowRight className="w-2.5 h-2.5" />
      </button>

      {isOpen && (
        <div
          className={`absolute bottom-full mb-2 w-80 p-4 rounded-lg shadow-xl z-50 animate-fade-in border ${
            isMobile ? 'right-0 left-auto' : 'left-0'
          }`}
          style={{
            backgroundColor: 'var(--bg)',
            borderColor: 'var(--border)',
            color: 'var(--text)',
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <div className="space-y-3">
            {/* Workflow Steps */}
            <div className="space-y-2">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                  1
                </div>
                <div>
                  <p className="text-xs font-medium">Submit your inquiry</p>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    Fill out the form with your requirements
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                  2
                </div>
                <div>
                  <p className="text-xs font-medium">Get a response</p>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    We'll respond within 24 hours
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-blue-500 text-white text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">
                  3
                </div>
                <div>
                  <p className="text-xs font-medium">Start your subscription</p>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    Get started with your chosen plan
                  </p>
                </div>
              </div>
            </div>

            {/* Email Guidelines - Important */}
            <div
              className="pt-2 mt-1 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-start gap-2">
                <FiAlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p
                    className="text-xs font-semibold"
                    style={{ color: '#f59e0b' }}
                  >
                    Important: Check Your Spam Folder
                  </p>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    Our response emails may go to your spam folder. Please check
                    there if you don't see our reply in your inbox.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2 mt-1.5">
                <FiMail className="w-3.5 h-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    <span className="font-medium">Alternative:</span> Provide
                    your phone number or WhatsApp in the message so we can
                    contact you directly if needed.
                  </p>
                </div>
              </div>
            </div>

            <div
              className="pt-1.5 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <p
                className="text-[10px] italic"
                style={{ color: 'var(--muted)' }}
              >
                💡 No automated ordering process. We collaborate manually and
                complete orders through direct communication.
              </p>
            </div>
          </div>
          {/* Tooltip arrow */}
          <div
            className={`absolute top-full w-2.5 h-2.5 rotate-45 -mt-1.5 ${
              isMobile ? 'right-6 left-auto' : 'left-6'
            }`}
            style={{
              backgroundColor: 'var(--bg)',
              borderRight: '1px solid var(--border)',
              borderBottom: '1px solid var(--border)',
            }}
          />
        </div>
      )}
    </div>
  );
};

// =============================================
// INQUIRY FORM COMPONENT - Enhanced Styling
// =============================================
const InquiryForm = ({ plan, onSuccess, billingCycle }) => {
  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [focusedField, setFocusedField] = useState(null);
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [showSpamAlert, setShowSpamAlert] = useState(true);

  const [emailValidation, setEmailValidation] = useState({
    isValid: false,
    errorMessage: null,
    suggestion: null,
    qualityScore: 0,
    isValidating: false,
  });

  const [touchedFields, setTouchedFields] = useState({
    name: false,
    email: false,
    phone: false,
    message: false,
  });

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMessage) setErrorMessage('');
    if (submitStatus === 'error') setSubmitStatus(null);
  };

  const handleBlur = fieldName => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  const handleEmailValidationChange = (isValid, validationResult) => {
    setEmailValidation({
      isValid: isValid,
      errorMessage: validationResult?.reason || null,
      suggestion: validationResult?.suggestions || null,
      qualityScore: validationResult?.score || 0,
      isValidating: false,
    });

    if (isValid && errorMessage && errorMessage.includes('email')) {
      setErrorMessage('');
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      return false;
    }

    if (!emailValidation.isValid) {
      setErrorMessage(
        emailValidation.errorMessage || 'Please enter a valid email address',
      );
      return false;
    }

    if (!formData.message.trim()) {
      setErrorMessage('Please tell us about your project or requirements');
      return false;
    }

    if (formData.message.trim().length < 20) {
      setErrorMessage('Please provide more details (minimum 20 characters)');
      return false;
    }

    return true;
  };

  // ==================== INQUIRY FORM SUBMIT - FIXED ====================
  const handleSubmit = async e => {
    e.preventDefault();

    setTouchedFields({
      name: true,
      email: true,
      phone: true,
      message: true,
    });

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      // FIX: Build packageData with price as a direct number
      const priceValue =
        billingCycle === 'monthly'
          ? plan.price?.monthly || plan.monthlyPrice || 0
          : plan.price?.yearly || plan.yearlyPrice || 0;

      const packageData = {
        id: plan.id,
        name: plan.name,
        price: priceValue, // FIX: Direct number value
        duration:
          billingCycle === 'monthly'
            ? plan.duration?.monthly || plan.monthlyDuration || 'Month to month'
            : plan.duration?.yearly ||
              plan.yearlyDuration ||
              'Annual commitment',
        features: plan.features || [],
        gradient: plan.gradient || 'from-purple-500 to-pink-500',
        imageUrl: plan.imageUrl || null,
        category: plan.category || 'subscription',
        popular: plan.popular || false,
        billingCycle: billingCycle,
        // Also pass the original price structure for reference
        originalPrice: plan.price || {
          monthly: plan.monthlyPrice,
          yearly: plan.yearlyPrice,
        },
        monthlyPrice: plan.monthlyPrice || plan.price?.monthly || 0,
        yearlyPrice: plan.yearlyPrice || plan.price?.yearly || 0,
      };

      const inquiryData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        company: formData.company.trim() || '',
        phone: formData.phone.trim() || '',
        message: formData.message.trim(),
      };

      const result = await subscriptionService.submitInquiry(
        inquiryData,
        packageData,
        billingCycle,
      );

      console.log('✅ Inquiry submitted successfully:', result);
      setSubmitStatus('success');

      if (onSuccess) onSuccess();

      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: '',
        });
        setSubmitStatus(null);
      }, 5000);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitStatus('error');
      setErrorMessage(
        error.message || 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid =
    formData.name.trim() &&
    emailValidation.isValid &&
    formData.message.trim() &&
    formData.message.trim().length >= 20;

  return (
    <div className="space-y-3">
      {/* Spam Alert Banner - Compact */}
      {showSpamAlert && (
        <div
          className="p-2.5 rounded-lg border animate-fade-in"
          style={{
            backgroundColor: 'rgba(245, 158, 11, 0.06)',
            borderColor: 'rgba(245, 158, 11, 0.2)',
          }}
        >
          <div className="flex items-start gap-2">
            <FiAlertTriangle className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p
                className="text-[11px] font-medium"
                style={{ color: '#f59e0b' }}
              >
                📬 Check Your Spam Folder
              </p>
              <p
                className="text-[10px] leading-relaxed"
                style={{ color: 'var(--muted)' }}
              >
                Our response may go to spam. Check there if you don't see our
                reply.
              </p>
              {/* Pro Tip with explanation */}
              <div className="flex items-start gap-1.5 mt-1 p-1.5 rounded bg-blue-500/5 border border-blue-500/10">
                <FiMail className="w-3 h-3 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                    <span className="font-medium text-blue-500">Pro tip:</span>{' '}
                    Add{' '}
                    <span className="text-blue-500 font-mono text-[9px]">
                      wdhejbul@gmail.com
                    </span>{' '}
                    to your contacts — this tells your email provider
                    <span className="text-[9px] block mt-0.5 text-blue-400/70">
                      (Gmail, Outlook, etc.) that we're a trusted sender, so our
                      replies go straight to your inbox, not spam.
                    </span>
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSpamAlert(false)}
                className="text-[9px] mt-1 hover:underline opacity-60"
                style={{ color: 'var(--muted)' }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-2.5">
        {/* Name - Compact */}
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onFocus={() => setFocusedField('name')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('name');
            }}
            placeholder="Full name *"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${touchedFields.name && !formData.name ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--text)',
              height: '36px',
            }}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.name && !formData.name && (
              <div className="text-[9px] text-red-500">Name is required</div>
            )}
          </div>
        </div>

        {/* Email - Compact */}
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <label
              className="text-[10px] font-medium"
              style={{ color: 'var(--text)' }}
            >
              Email <span className="text-red-500">*</span>
            </label>
            <div
              className="relative"
              onMouseEnter={() => setShowEmailTooltip(true)}
              onMouseLeave={() => setShowEmailTooltip(false)}
            >
              <FiInfo
                className="w-3 h-3 cursor-help"
                style={{ color: '#3b82f6' }}
              />
              {showEmailTooltip && (
                <div
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 p-2 rounded-lg shadow-lg z-50 animate-fade-in"
                  style={{
                    backgroundColor: 'var(--bg)',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <div className="text-[9px] space-y-0.5">
                    <p className="font-semibold text-[10px]">
                      Why we need your email?
                    </p>
                    <p>• Get confirmation of your inquiry</p>
                    <p>• Receive updates about your request</p>
                    <p>• Get our response within 24 hours</p>
                    <p className="text-yellow-500 mt-1 text-[9px]">
                      ⚠️ Check spam if you don't see our reply!
                    </p>
                  </div>
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 rotate-45"
                    style={{
                      backgroundColor: 'var(--bg)',
                      borderRight: '1px solid var(--border)',
                      borderBottom: '1px solid var(--border)',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
          <EmailValidationInput
            value={formData.email}
            onChange={e => {
              setFormData(prev => ({ ...prev, email: e.target.value }));
              if (errorMessage && errorMessage.includes('email'))
                setErrorMessage('');
            }}
            onBlur={() => handleBlur('email')}
            placeholder="your@email.com"
            className="w-full"
            inputClassName="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px] h-[36px]"
            onValidationChange={handleEmailValidationChange}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.email && !emailValidation.isValid && (
              <div className="text-[9px] text-red-500">
                {emailValidation.errorMessage ||
                  'Please enter a valid email address'}
              </div>
            )}
          </div>
        </div>

        {/* Phone - Compact */}
        <div>
          <div className="flex items-center gap-1.5 mb-0.5">
            <label
              className="text-[10px] font-medium"
              style={{ color: 'var(--text)' }}
            >
              Phone{' '}
              <span className="text-yellow-500 text-[9px]">(recommended)</span>
            </label>
            <FiInfo
              className="w-3 h-3 cursor-help"
              style={{ color: 'var(--muted)' }}
              title="Providing your phone number allows us to contact you directly if our email goes to spam."
            />
          </div>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            onFocus={() => setFocusedField('phone')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('phone');
            }}
            placeholder="+880 1884 521 488 (backup contact)"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              height: '36px',
            }}
          />
          <div className="h-3 mt-0.5">
            <p className="text-[9px]" style={{ color: 'var(--muted)' }}>
              <FiAlertTriangle className="inline w-2.5 h-2.5 text-yellow-500 mr-0.5" />
              Helps us contact you directly if our email goes to spam.
            </p>
          </div>
        </div>

        {/* Company - Compact */}
        <div>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            onFocus={() => setFocusedField('company')}
            onBlur={() => setFocusedField(null)}
            placeholder="Company (optional)"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid var(--border)',
              color: 'var(--text)',
              height: '36px',
            }}
          />
        </div>

        {/* Message - Compact */}
        <div>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            onFocus={() => setFocusedField('message')}
            onBlur={() => {
              setFocusedField(null);
              handleBlur('message');
            }}
            placeholder="Project details... (include phone/WhatsApp for direct contact)"
            rows="2"
            className="w-full px-3 py-1.5 rounded-lg outline-none transition-all resize-none text-[13px]"
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${touchedFields.message && (!formData.message || formData.message.length < 20) ? '#ef4444' : 'var(--border)'}`,
              color: 'var(--text)',
              minHeight: '56px',
            }}
          />
          <div className="h-3 mt-0.5">
            {touchedFields.message && !formData.message && (
              <div className="text-[9px] text-red-500">
                Please tell us about your project
              </div>
            )}
            {touchedFields.message &&
              formData.message &&
              formData.message.length < 20 && (
                <div className="text-[9px] text-yellow-500">
                  {20 - formData.message.length} more characters needed
                </div>
              )}
          </div>
        </div>

        {/* Error Message - Compact */}
        {errorMessage && (
          <div
            className="flex items-center gap-1.5 p-1.5 rounded-lg animate-fade-in"
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
            }}
          >
            <FiAlertCircle className="w-3 h-3 text-red-500 flex-shrink-0" />
            <span className="text-[9px] text-red-500">{errorMessage}</span>
          </div>
        )}

        {/* Success Message - Compact */}
        {submitStatus === 'success' && (
          <div
            className="p-2.5 rounded-lg animate-fade-in"
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.06)',
              border: '1px solid rgba(34, 197, 94, 0.2)',
            }}
          >
            <div className="flex items-start gap-2">
              <FiCheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] font-medium text-green-500">
                  Inquiry submitted!
                </p>
                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                  We'll respond within 24 hours.
                </p>
                <div className="flex items-center gap-1 mt-1 text-[9px] text-yellow-500">
                  <FiAlertTriangle className="w-2.5 h-2.5" />
                  <span>Check spam folder if you don't see our reply!</span>
                </div>
                {formData.phone && (
                  <p className="text-[9px] text-green-500 mt-0.5">
                    ✅ Phone provided for backup contact.
                  </p>
                )}
                {!formData.phone && (
                  <p className="text-[9px] text-yellow-500 mt-0.5">
                    ⚠️ No phone provided. Check spam or contact us directly.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Submit Button - Compact */}
        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg font-medium transition-all hover:opacity-80 disabled:opacity-50 text-[13px]"
          style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
        >
          {isSubmitting ? (
            <>
              <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              Send Inquiry
              <FiSend className="w-3.5 h-3.5" />
            </>
          )}
        </button>

        {/* Form Status - Compact */}
        <div
          className="flex items-center justify-center gap-2.5 text-[9px]"
          style={{ color: 'var(--muted)' }}
        >
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${formData.name.trim() ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Name
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${emailValidation.isValid ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Email
          </span>
          <span className="flex items-center gap-1">
            <span
              className={`w-1.5 h-1.5 rounded-full ${formData.message.trim().length >= 20 ? 'bg-green-500' : 'bg-gray-400'}`}
            />
            Message
          </span>
        </div>

        {/* Privacy Notice - Compact */}
        <div
          className="flex items-center justify-between text-[9px]"
          style={{ color: 'var(--muted)' }}
        >
          <span className="flex items-center gap-1">
            <FiCheck className="w-2.5 h-2.5 text-green-500" />
            Privacy secure
          </span>
          <WorkflowTooltip />
        </div>
      </form>
    </div>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
function SubscriptionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { actualTheme } = useTheme();
  const isDark = actualTheme === 'dark';

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inquirySuccess, setInquirySuccess] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  // Related plans
  const [relatedPlans, setRelatedPlans] = useState([]);

  // ✅ REMOVED: metaData - No client-side OG generation
  // Social media scrapers get OG tags from server API

  // Load plan
  useEffect(() => {
    const loadPlan = async () => {
      if (!id) {
        setError('No plan ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const q = query(
          collection(db, 'subscriptionPlans'),
          where('status', '==', 'active'),
          orderBy('order', 'asc'),
        );
        const snapshot = await getDocs(q);
        const plansList = [];

        snapshot.forEach(doc => {
          const data = doc.data();

          // FIX: Handle both old and new data structures
          const monthlyPrice = data.monthlyPrice || data.price?.monthly || 0;
          const yearlyPrice = data.yearlyPrice || data.price?.yearly || 0;

          plansList.push({
            id: doc.id,
            name: data.name,
            description: data.description,
            price: {
              monthly: monthlyPrice,
              yearly: yearlyPrice,
            },
            monthlyPrice: monthlyPrice, // Add direct access for compatibility
            yearlyPrice: yearlyPrice,
            duration: {
              monthly:
                data.monthlyDuration ||
                data.duration?.monthly ||
                'Month to month',
              yearly:
                data.yearlyDuration ||
                data.duration?.yearly ||
                'Annual commitment',
            },
            monthlyDuration:
              data.monthlyDuration ||
              data.duration?.monthly ||
              'Month to month',
            yearlyDuration:
              data.yearlyDuration ||
              data.duration?.yearly ||
              'Annual commitment',
            features: data.features || [],
            popular: data.popular || false,
            icon: data.icon || 'FiCalendar',
            ctaText: data.ctaText || 'Get Started',
            savingsBadge: data.savingsBadge || 'Save 15%',
            order: data.order || 0,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            imageUrl: data.imageUrl || null,
            gradient: data.gradient || 'from-purple-500 to-pink-500',
            category: data.category || 'subscription',
          });
        });

        const foundPlan = plansList.find(p => p.id === id);

        if (!foundPlan) {
          setError('Plan not found');
          setLoading(false);
          return;
        }

        setPlan(foundPlan);

        const related = plansList.filter(p => p.id !== id).slice(0, 3);
        setRelatedPlans(related);
      } catch (err) {
        console.error('Error loading plan:', err);
        setError(err.message || 'Failed to load plan');
      } finally {
        setLoading(false);
      }
    };

    loadPlan();
  }, [id]);

  const handleInquirySuccess = () => {
    setInquirySuccess(true);
    setTimeout(() => setInquirySuccess(false), 5000);
  };

  const calculateYearlySavings = (monthlyPrice, yearlyPrice) => {
    const yearlyTotal = monthlyPrice * 12;
    const savings = yearlyTotal - yearlyPrice;
    const savingsPercent = Math.round((savings / yearlyTotal) * 100);
    return savingsPercent > 0 ? savingsPercent : 0;
  };

  // Schema markup - Keep for SEO
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: plan?.name || 'Subscription Plan',
    description:
      plan?.description || 'Professional design subscription service',
    provider: { '@type': 'Person', name: 'WD Hajbul' },
    offers: {
      '@type': 'Offer',
      price:
        billingCycle === 'monthly' ? plan?.price?.monthly : plan?.price?.yearly,
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price:
          billingCycle === 'monthly'
            ? plan?.price?.monthly
            : plan?.price?.yearly,
        priceCurrency: 'USD',
        unitPricingMeasurement: {
          '@type': 'UnitPricingMeasurement',
          unitCode: billingCycle === 'monthly' ? 'MON' : 'ANN',
        },
      },
    },
  };

  if (loading) {
    return <SubscriptionDetailSkeleton />;
  }

  if (error || !plan) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div className="text-center p-8">
          <h2
            className="text-2xl font-bold mb-4"
            style={{ color: 'var(--text)' }}
          >
            {error || 'Plan not found'}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
            The subscription plan you're looking for doesn't exist or has been
            removed.
          </p>
          <Link
            to="/subscription"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-sm transition-all hover:gap-3"
            style={{ backgroundColor: 'var(--text)', color: 'var(--bg)' }}
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Plans
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `${window.location.origin}/subscription/${plan.id}`;
  const pageTitle = plan.name;
  const pageDescription = plan.description;
  const IconComponent = getIconComponent(plan.icon);
  const savingsPercent = calculateYearlySavings(
    plan.price.monthly,
    plan.price.yearly,
  );

  return (
    <>
      {/*
        ✅ HELMET - Only basic page info
        ❌ NO OG TAGS - Server handles OG generation via API
        Social media scrapers get OG from /api/subscription-plans?action=social-preview&id=xxx
      */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`subscription, ${plan.name}, monthly, yearly, design subscription`}
        />
        <link rel="canonical" href={shareUrl} />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div
        className="min-h-screen"
        style={{ backgroundColor: 'var(--bg)', color: 'var(--text)' }}
      >
        {/* Sticky Navigation */}
        <div
          className="sticky top-[85px] z-30 backdrop-blur-md border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
            <Link
              to="/subscription"
              className="flex items-center gap-2 text-sm transition-all hover:gap-3"
              style={{ color: 'var(--muted)' }}
            >
              <FiArrowLeft className="w-4 h-4" />
              Back to Plans
            </Link>
            <div className="flex items-center gap-3">
              <div
                className="flex items-center gap-1 p-0.5 rounded-full"
                style={{ backgroundColor: 'var(--border)' }}
              >
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${billingCycle === 'monthly' ? 'bg-black dark:bg-white text-white dark:text-black' : ''}`}
                  style={{
                    backgroundColor:
                      billingCycle === 'monthly'
                        ? isDark
                          ? '#ffffff'
                          : '#000000'
                        : 'transparent',
                    color:
                      billingCycle === 'monthly'
                        ? isDark
                          ? '#000000'
                          : '#ffffff'
                        : 'var(--text)',
                  }}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingCycle('yearly')}
                  className={`px-3 py-1 rounded-full text-xs transition-all ${billingCycle === 'yearly' ? 'bg-black dark:bg-white text-white dark:text-black' : ''}`}
                  style={{
                    backgroundColor:
                      billingCycle === 'yearly'
                        ? isDark
                          ? '#ffffff'
                          : '#000000'
                        : 'transparent',
                    color:
                      billingCycle === 'yearly'
                        ? isDark
                          ? '#000000'
                          : '#ffffff'
                        : 'var(--text)',
                  }}
                >
                  Yearly
                </button>
              </div>
              <ShareButtons
                url={shareUrl}
                title={plan.name}
                description={plan.description}
                imageUrl={plan.imageUrl || ''}
                plan={plan}
              />
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column - Main Content (8 columns) */}
            <div className="lg:col-span-8 space-y-6">
              {/* Icon & Title */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="flex items-center gap-4"
              >
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border)' }}
                >
                  <IconComponent
                    className="w-8 h-8"
                    style={{ color: 'var(--text)' }}
                  />
                </div>
                <div>
                  <h1
                    className="text-2xl md:text-3xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    {plan.name}
                  </h1>
                  <div
                    className="flex flex-wrap gap-3 mt-1 text-xs"
                    style={{ color: 'var(--muted)' }}
                  >
                    <span className="flex items-center gap-1.5">
                      <FiCalendar className="w-3.5 h-3.5" />
                      {billingCycle === 'monthly'
                        ? plan.duration.monthly
                        : plan.duration.yearly}
                    </span>
                    {plan.popular && (
                      <span className="flex items-center gap-1.5 text-yellow-500">
                        <FiStar className="w-3.5 h-3.5" />
                        Most Popular
                      </span>
                    )}
                    {billingCycle === 'yearly' && savingsPercent > 0 && (
                      <span className="flex items-center gap-1.5 text-green-500">
                        Save {savingsPercent}% annually
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Description */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-6 rounded-full bg-blue-500" />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    About This Plan
                  </h2>
                </div>
                <p
                  className="leading-relaxed whitespace-pre-wrap"
                  style={{ color: 'var(--muted)' }}
                >
                  {plan.description}
                </p>
              </motion.div>

              {/* Pricing Cards */}
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                <motion.div
                  variants={fadeInUp}
                  className={`p-4 rounded-xl border ${billingCycle === 'monthly' ? 'ring-2' : ''}`}
                  style={{
                    borderColor: 'var(--border)',
                    ringColor: 'var(--text)',
                  }}
                >
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    Monthly Plan
                  </p>
                  <p
                    className="text-3xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    ${plan.price.monthly}
                    <span
                      className="text-sm font-normal"
                      style={{ color: 'var(--muted)' }}
                    >
                      /mo
                    </span>
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    {plan.duration.monthly}
                  </p>
                </motion.div>
                <motion.div
                  variants={fadeInUp}
                  className={`p-4 rounded-xl border ${billingCycle === 'yearly' ? 'ring-2' : ''}`}
                  style={{
                    borderColor: 'var(--border)',
                    ringColor: 'var(--text)',
                  }}
                >
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>
                    Yearly Plan
                  </p>
                  <p
                    className="text-3xl font-bold"
                    style={{ color: 'var(--text)' }}
                  >
                    ${plan.price.yearly}
                    <span
                      className="text-sm font-normal"
                      style={{ color: 'var(--muted)' }}
                    >
                      /yr
                    </span>
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
                    {plan.duration.yearly}
                  </p>
                  {savingsPercent > 0 && (
                    <p className="text-xs text-green-500 mt-1">
                      Save {savingsPercent}% annually
                    </p>
                  )}
                </motion.div>
              </motion.div>

              {/* Features */}
              {plan.features && plan.features.length > 0 && (
                <motion.div
                  initial="hidden"
                  animate="visible"
                  variants={fadeInUp}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-1 h-6 rounded-full bg-green-500" />
                    <h2
                      className="text-lg font-semibold"
                      style={{ color: 'var(--text)' }}
                    >
                      What's Included
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-sm">
                        <FiCheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span style={{ color: 'var(--text)' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Benefits Summary */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1 h-6 rounded-full bg-purple-500" />
                  <h2
                    className="text-lg font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Why Subscribe
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { icon: FiTrendingUp, label: 'Scale Your Business' },
                    { icon: FiShield, label: 'Secure & Reliable' },
                    { icon: FiUsers, label: 'Team Collaboration' },
                    { icon: FiHeart, label: 'Priority Support' },
                  ].map((benefit, idx) => (
                    <div
                      key={idx}
                      className="text-center p-3 rounded-xl border"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <benefit.icon
                        className="w-6 h-6 mx-auto mb-1"
                        style={{ color: 'var(--text)' }}
                      />
                      <p
                        className="text-xs font-medium"
                        style={{ color: 'var(--text)' }}
                      >
                        {benefit.label}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Inline Ad */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInUp}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[60px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Sidebar (4 columns) */}
            <div className="lg:col-span-4 space-y-6">
              {/* Fiverr Card */}
              <FiverrCard variant="sidebar" />

              {/* Inquiry Form - Enhanced with Email Guidelines */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <div
                    className="p-1.5 rounded-lg"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <FiMessageCircle className="w-4 h-4 text-blue-500" />
                  </div>
                  <h3
                    className="text-sm font-semibold"
                    style={{ color: 'var(--text)' }}
                  >
                    Send Inquiry
                  </h3>
                </div>
                <InquiryForm
                  plan={plan}
                  onSuccess={handleInquirySuccess}
                  billingCycle={billingCycle}
                />
              </motion.div>

              {/* Google Ad Space */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-3 rounded-xl border border-dashed text-center"
                style={{ borderColor: 'var(--border)' }}
              >
                <div
                  className="flex items-center justify-center gap-2 text-[10px]"
                  style={{ color: 'var(--muted)' }}
                >
                  <span>📢</span>
                  <span>Advertisement</span>
                </div>
                <div
                  className="min-h-[250px] flex items-center justify-center"
                  style={{ backgroundColor: 'var(--border-light)' }}
                >
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    Google Ad Space
                  </span>
                </div>
              </motion.div>

              {/* Plan Details */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border"
                style={{ borderColor: 'var(--border)' }}
              >
                <h3
                  className="text-sm font-semibold mb-3"
                  style={{ color: 'var(--text)' }}
                >
                  <FiFolder className="inline mr-2" />
                  Plan Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Plan Name</span>
                    <span style={{ color: 'var(--text)' }}>{plan.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Monthly Price</span>
                    <span style={{ color: 'var(--text)' }}>
                      ${plan.price.monthly}/mo
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Yearly Price</span>
                    <span style={{ color: 'var(--text)' }}>
                      ${plan.price.yearly}/yr
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span style={{ color: 'var(--muted)' }}>Features</span>
                    <span style={{ color: 'var(--text)' }}>
                      {plan.features?.length || 0}
                    </span>
                  </div>
                  {plan.popular && (
                    <div className="flex justify-between">
                      <span style={{ color: 'var(--muted)' }}>Status</span>
                      <span className="text-yellow-500 flex items-center gap-1">
                        <FiStar className="w-3 h-3" /> Most Popular
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Have questions? Contact Us */}
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeInRight}
                className="p-4 rounded-xl border text-center"
                style={{
                  borderColor: 'var(--border)',
                  backgroundColor: 'var(--border-light)',
                }}
              >
                <div className="flex items-center justify-center mb-2">
                  <div
                    className="p-2 rounded-full"
                    style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}
                  >
                    <FiMessageCircle className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <h3
                  className="text-sm font-semibold mb-1"
                  style={{ color: 'var(--text)' }}
                >
                  Have any questions?
                </h3>
                <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                  We're here to help. Reach out and we'll get back to you within
                  24 hours.
                </p>
                <div className="flex flex-col gap-2">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all hover:gap-3"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiMessageCircle className="w-3.5 h-3.5" />
                    Contact Us
                    <FiArrowRight className="w-3 h-3" />
                  </Link>
                  <div
                    className="flex items-center justify-center gap-4 text-[10px]"
                    style={{ color: 'var(--muted)' }}
                  >
                    <a
                      href="tel:+8801884521448"
                      className="flex items-center gap-1 cursor-pointer"
                    >
                      <FiPhone className="w-3 h-3" />
                      +8801884521448
                    </a>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <FiClock className="w-3 h-3" />
                      Worldwide available remotely
                    </span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Related Plans */}
          {relatedPlans.length > 0 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
              className="mt-8 pt-8 border-t"
              style={{ borderColor: 'var(--border)' }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 rounded-full bg-gradient-to-b from-blue-500 to-purple-500" />
                <h2
                  className="text-xl font-bold"
                  style={{ color: 'var(--text)' }}
                >
                  Explore Other Plans
                </h2>
                <span
                  className="text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: 'var(--border)',
                    color: 'var(--muted)',
                  }}
                >
                  Compare
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedPlans.map(related => (
                  <div
                    key={related.id}
                    onClick={() => navigate(`/subscription/${related.id}`)}
                    className="group cursor-pointer rounded-xl overflow-hidden border transition-all hover:shadow-md hover:-translate-y-0.5 p-4"
                    style={{ borderColor: 'var(--border)' }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: 'var(--border)' }}
                      >
                        {React.createElement(getIconComponent(related.icon), {
                          className: 'w-5 h-5',
                          style: { color: 'var(--text)' },
                        })}
                      </div>
                      <div>
                        <p
                          className="text-sm font-medium"
                          style={{ color: 'var(--text)' }}
                        >
                          {related.name}
                        </p>
                        <div
                          className="flex items-center gap-2 text-xs"
                          style={{ color: 'var(--muted)' }}
                        >
                          <span>${related.price.monthly}/mo</span>
                          {related.popular && (
                            <span className="text-yellow-500 flex items-center gap-0.5">
                              <FiStar className="w-2.5 h-2.5" /> Popular
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p
                      className="text-xs line-clamp-2"
                      style={{ color: 'var(--muted)' }}
                    >
                      {related.description}
                    </p>
                    <button
                      className="mt-3 text-xs flex items-center gap-1 transition-all hover:gap-2"
                      style={{ color: 'var(--text)' }}
                    >
                      View Details <FiArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Bottom Ad */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="mt-8 p-4 rounded-xl border border-dashed text-center"
            style={{ borderColor: 'var(--border)' }}
          >
            <div
              className="flex items-center justify-center gap-2 text-[10px]"
              style={{ color: 'var(--muted)' }}
            >
              <span>📢</span>
              <span>Advertisement</span>
            </div>
            <div
              className="min-h-[90px] flex items-center justify-center"
              style={{ backgroundColor: 'var(--border-light)' }}
            >
              <span className="text-xs" style={{ color: 'var(--muted)' }}>
                Google Ad Space
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <style jsx>{`
        .gradient-border {
          position: relative;
        }
        .gradient-border::before {
          content: '';
          position: absolute;
          inset: -1px;
          border-radius: 12px;
          padding: 1px;
          background: linear-gradient(135deg, #22c55e, #3b82f6, #8b5cf6);
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
          opacity: 0.3;
          transition: opacity 0.5s ease;
        }
        .gradient-border:hover::before {
          opacity: 0.8;
        }
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </>
  );
}

export default SubscriptionDetail;
